<!--
  File name: DataBreachSettings.vue
  Program Description:.
    - This component allows the user to view and edit the current settings of DBN
-->
<template>
  <div class="page">
    <v-row
      justify="center"
    >
      <v-col
        class="pa-0 pb-5"
      >
        <v-card
          tile
          flat
          :loading="isLoading"
        >
          <v-toolbar
            color="#d0d3d9"
            flat
          >
            <v-toolbar-title
              class="text-h4 font-weight-bold "
            >
              DBN Settings
            </v-toolbar-title>
            <v-spacer />
            <v-breadcrumbs
              large
              :items="breadcrumbs.items"
              :divider="breadcrumbs.divider"
            />
          </v-toolbar>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="container"
    >
      <v-col>
        <v-navigation-drawer
          width="100%"
          class="rounded"
          style="border-right: 1px solid #cdcdcd"
          :style="`background-color: ${$metaData.app.login.background};`"
        >
          <v-list
            class="pa-0"
          >
            <v-list-item-group v-model="selected">
              <v-list-item
                id="gen-info-tab"
                link
                class="py-1"
                active-class="primary--text"
                @click="switchTab('dbnSettings')"
              >
                <v-list-item-action>
                  <v-icon>mdi-cog</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    Show Cause Order
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-navigation-drawer>
      </v-col>
      <v-col
        cols="8"
        class="pa-0"
        align-self="start"
        justify="center"
      >
        <template v-if="dbnSettings">
          <v-card
            :class="'py-13 px-15 rounded radius shadow'"
          >
            <v-row
              class="pb-3 text-h5 primary--text"
            >
              <span
                style="margin:auto; max-width: 50%"
                class="font-weight-bold"
              >
                Deadline for appearance stated on the Show Cause Order
              </span>
              <v-spacer />
              <div
                :style="'width: 30%; margin-right: 80px;'"
                :class="'d-flex justify-end mr-5'"
              >
                <v-btn
                  v-if="readOnly && showLabel('canPatchDaysSetting')"
                  id="edit-button"
                  style="background-color:#162b54; color: #FFF; "
                  class="font-weight-regular rounded-lg editBtn"
                  type="button"
                  :loading="isLoading"
                  @click="readOnly = false"
                >
                  <v-icon
                    class="pr-2"
                    style="font-size: 18px"
                  >
                    mdi-pencil
                  </v-icon>
                  {{ getLabel('canPatchDaysSetting', 'Edit') }}
                </v-btn>
              </div>
            </v-row>
            <v-row class="pt-8">
              <v-col
                cols="2"
                class="pa-0"
              >
                <v-text-field
                  id="days-field"
                  v-model="formItems.days"
                  label="Days"
                  placeholder="Days"
                  dense
                  outlined
                  :disabled="readOnly"
                  :error-messages="genericError('formItems.days', 'Days')"
                  @blur="$v.formItems.days.$touch()"
                  @input="$v.formItems.days.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                class="pl-0"
              >
                <v-text-field
                  id="address-field"
                  v-model="formItems.address"
                  label="Address"
                  placeholder="Address"
                  dense
                  outlined
                  :disabled="readOnly"
                  :error-messages="genericError('formItems.address', 'Address')"
                  @blur="$v.formItems.address.$touch()"
                  @input="$v.formItems.address.$touch()"
                />
              </v-col>
            </v-row>
            <v-card-actions>
              <v-spacer />
              <template>
                <v-btn
                  v-if="!readOnly"
                  id="cancel-update-btn"
                  text
                  class=" primary--text font-weight-black mr-3"
                  @click=" readOnly = true, getSettings()"
                >
                  Cancel
                </v-btn>
                <v-btn
                  v-if="showLabel('canPatchDaysSetting') && !readOnly"
                  id="update-btn"
                  style="background-color:#162b54; color: #FFF; "
                  text
                  type="button"
                  :loading="isLoading"
                  @click="confirm()"
                >
                  {{ getLabel('canPatchDaysSetting', 'Update') }}
                </v-btn>
              </template>
            </v-card-actions>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import pdbnf from '@/utils/webapi/pdbnf'
import { privilegeMixin } from 'session-plugin'
import { validationMixin, custom, validationErrors } from '@/utils/validations'

export default {
  name: 'DataBreachSettings',
  components: {
  },
  mixins: [privilegeMixin, validationMixin],
  data: () => ({
    formItems: {
      days: 0,
      address: ''
    },
    dbnSettings: true,
    selected: 0,
    isLoading: false,
    readOnly: true
  }
  ),
  validations () {
    return {
      formItems: custom.pdbnfSettings
    }
  },
  computed: {
    ...validationErrors,
    breadcrumbs () {
      return {
        divider: '>',
        large: true,
        items: [
          {
            text: 'DBN'
          },
          {
            text: 'Settings',
            to: {
              name: 'DataBreachSettings'
            }
          }
        ]
      }
    }
  },
  async created () {
    await this.getSettings()
  },
  methods: {
    // fetch settings from backend
    async getSettings () {
      const resp = await pdbnf.viewSettings()
      const data = await resp.json()
      this.$set(this.formItems, 'days', data.entry.days)
      this.$set(this.formItems, 'address', data.entry.address)
    },
    // update settings
    async update () {
      const body = {
        days: this.formItems.days,
        address: this.formItems.address
      }
      this.readOnly = true
      const resp = await pdbnf.patchSettings(body)
      const data = await resp.json()
      this.triggerAlert({ success: resp.ok, action: 'update', data })
    },
    // show success or error message
    triggerAlert ({ success, data }) {
      if (success) {
        this.$store.dispatch('showAlert', {
          message: 'Deadline for Show Cause order has been changed successfully!',
          type: 'success'
        })
        this.readOnly = true
      } else {
        this.$store.dispatch('showAlert', {
          message: data.error || data.message,
          type: 'error'
        })
        this.readOnly = false
      }
    },
    // trigger validations
    // show confirm dialog when valid, else, show error
    confirm () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.confirmDialog()
      } else {
        this.$store.dispatch('showAlert', {
          message: 'Required fields cannot be blank.',
          type: 'error'
        })
      }
    },
    // show confirmation dialog
    confirmDialog () {
      this.reason = 'action'
      this.$store.dispatch('showDialog', {
        title: 'Update Settings',
        message: 'Are you sure you want to update the deadline for Show Cause Order?',
        buttonLabel: 'Update',
        buttonColor: 'primary',
        callback: () => { this.update() }
      })
    },
    switchTab (tab) {
      if (tab === 'dbnSettings') {
        this.dbnSettings = true
      }
    }
  }

}

</script>

<style scoped>
.page {
  display: grid;
  justify-self: center;
  margin: auto;
  max-width:1170px;
}
.shadow {
    box-shadow:4px 8px 8px -2px #c3c3c3 !important;
}
.radius {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
.form >>> .v-card {
  padding: 10% 5% 0% 5%;
  border-radius: 100%;
}
.container {
  padding:1% 0%;
  margin-right: 0;
  width: 100%;
}
.btn--fab {
  margin-bottom: 50px;
}
.theme--light.v-text-field.v-input--is-disabled >>> .v-input__slot::before{
  border: none !important;
}

.v-text-field >>> .v-input__slot::after{
  border:none !important;
}

.v-breadcrumbs >>> a{
  color: #162b54 !important;
  font-weight: bolder
}
.v-breadcrumbs >>> {
  font-weight: bolder
}
.cancel-btn{
  position: absolute;
  bottom:52.5px;
  right:220px
}
</style>
